import { useState } from 'react'
import { Link } from 'react-router'
import { AlertTriangle, ArrowLeft, CheckCircle } from 'lucide-react'
import apiClient from '../../api/client'
import type { AxiosError } from 'axios'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await apiClient.post('/auth/forgot-password', { email })
      setSent(true)
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>
      if (axiosError.response?.status === 404) {
        // Non rivelare se l'email esiste o meno
        setSent(true)
      } else {
        setError(axiosError.response?.data?.message || 'Errore durante l\'invio. Riprova.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="relative flex min-h-dvh w-full items-center justify-center overflow-hidden bg-surface p-4 text-heading">
      <div className="pointer-events-none absolute top-6 left-8 flex items-center gap-2.5">
        <img src="/apple-touch-icon.png" alt="" className="h-5 w-5 rounded-md" />
        <span className="font-mono text-xs tracking-wider text-link uppercase">DomusAurea</span>
      </div>
      <span className="pointer-events-none absolute top-6 right-8 font-mono text-xs tracking-wider text-link">Φ 1.618</span>
      <span className="pointer-events-none absolute right-8 bottom-6 font-mono text-xs tracking-wider text-link">© 2026 DomusAurea</span>
      <div className="pointer-events-none absolute top-1/2 right-0 left-0 h-px -translate-y-1/2 bg-heading/10" />
      <div className="pointer-events-none absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-heading/10" />

      <section className="animate-panel-in relative z-10 my-8 w-full max-w-100 border border-border-subtle bg-canvas p-8 opacity-0 shadow-2xl sm:p-11">
        <div className="mb-7">
          <p className="mb-2 font-mono text-[11px] tracking-[0.16em] text-link uppercase">Recupero account</p>
          <h1 className="mb-2 text-2xl font-semibold [font-variant-caps:small-caps]">Password dimenticata</h1>
          <p className="mb-0 text-sm leading-relaxed text-muted">Inserisci l&apos;indirizzo email associato al tuo account. Ti invieremo le istruzioni per reimpostare la password.</p>
        </div>

        {sent ? (
          <div aria-live="polite">
            <div className="mb-5 flex items-start gap-2.5 border border-success-200 border-l-4 border-l-success-500 bg-success-50 px-3.5 py-3 text-sm leading-relaxed text-positive" role="status">
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Se l&apos;email è registrata, riceverai le istruzioni per il reset.</span>
            </div>
            <Link
              to="/login"
              className="inline-flex h-13 w-full items-center justify-center gap-2 bg-heading text-sm font-semibold text-on-dark transition-all hover:-translate-y-px hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
            >
              <ArrowLeft className="h-4 w-4" />
              Torna al login
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-5 flex items-start gap-2.5 border border-danger-200 border-l-4 border-l-danger-500 bg-danger-50 px-3.5 py-3 text-sm leading-relaxed text-negative" role="alert">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-xs tracking-wide text-muted uppercase">Email</label>
                <input
                  type="email"
                  className="h-12 w-full border border-border-subtle bg-surface px-4 text-base text-heading transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 focus:outline-none sm:text-sm"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder="nome@esempio.it"
                />
              </div>

              <button
                type="submit"
                className="flex h-13 w-full items-center justify-center gap-2 bg-heading text-sm font-semibold text-on-dark transition-all hover:-translate-y-px hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-default disabled:opacity-75 disabled:shadow-none disabled:hover:translate-y-0"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-secondary-50/35 border-t-secondary-50" role="status" aria-hidden="true" />
                    Invio in corso...
                  </>
                ) : (
                  'Invia istruzioni'
                )}
              </button>
            </form>

            <div className="pt-1 text-center">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-muted hover:text-heading hover:underline">
                <ArrowLeft className="h-3.5 w-3.5" />
                Torna al login
              </Link>
            </div>
          </>
        )}
      </section>
    </main>
  )
}
